import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@ludolio/shared-ui';
import { EmptyState, PageHeader, TeamRoleBadge } from '@/components/common';
import { getErrorMessage, labsApi } from '@/lib/api';
import { useLabs } from '@/lib/labs-context';
import type { LabTeamSummary } from '@/lib/types';

export const Route = createFileRoute('/$orgSlug/teams/')({
  component: TeamsPage,
});

function TeamsPage() {
  const labs = useLabs();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [teams, setTeams] = useState<LabTeamSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(24);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let active = true;
    labsApi
      .listTeams(labs.organization.slug, page, 24)
      .then((result) => {
        if (!active) return;
        setTeams(result.teams);
        setTotal(result.total);
        setPageSize(result.pageSize);
        setError('');
      })
      .catch((err) => {
        if (active) setError(getErrorMessage(err));
      });
    return () => {
      active = false;
    };
  }, [labs.organization.slug, page]);

  const newTeamButton = (
    <Button onClick={() => setCreating(true)}>
      <Plus />
      New team
    </Button>
  );
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <PageHeader title="Teams" actions={teams && teams.length > 0 ? newTeamButton : undefined} />
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {!teams && !error && <p className="text-sm text-muted-foreground">Loading…</p>}
      {teams?.length === 0 && <EmptyState title="You are not on a team yet" action={newTeamButton} />}

      {teams && teams.length > 0 && (
        <ul className="divide-y rounded-lg border">
          {teams.map((team) => (
            <li key={team.id}>
              <Link
                to="/$orgSlug/teams/$teamId"
                params={{ orgSlug: labs.organization.slug, teamId: team.id }}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 p-4 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="min-w-0">
                  <h2 className="truncate font-medium">{team.name}</h2>
                  <p className="min-h-5 truncate text-sm text-muted-foreground">{team.description}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <TeamRoleBadge role={team.role} />
                  <span className="text-xs text-muted-foreground">
                    {team.projectCount} {team.projectCount === 1 ? 'project' : 'projects'}
                    {' · '}
                    {team.memberCount} {team.memberCount === 1 ? 'member' : 'members'}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {pageCount > 1 && (
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
            Previous
          </Button>
          <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}>
            Next
          </Button>
        </div>
      )}

      <CreateTeamDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(teamId) => {
          setCreating(false);
          navigate({
            to: '/$orgSlug/teams/$teamId',
            params: { orgSlug: labs.organization.slug, teamId },
          });
        }}
      />
    </>
  );
}

function CreateTeamDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (teamId: string) => void;
}) {
  const labs = useLabs();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setName('');
    setDescription('');
    setError('');
  }, [open]);

  const create = async () => {
    setSaving(true);
    setError('');
    try {
      const team = await labsApi.createTeam(labs.organization.slug, name.trim(), description.trim() || null);
      onCreated(team.team.id);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <DialogHeader>
            <DialogTitle>New team</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="team-name">Name</Label>
            <Input id="team-name" value={name} maxLength={120} onChange={(event) => setName(event.target.value)} autoFocus />
          </div>
          <div className="space-y-2">
            <Label htmlFor="team-description">Description</Label>
            <Textarea
              id="team-description"
              value={description}
              maxLength={2000}
              rows={3}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || !name.trim()}>{saving ? 'Creating…' : 'Create team'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
